import React from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    Link as MuiLink,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import {Link as RouterLink} from 'react-router';
import {useCarver, useCarverHistory} from '../../../hooks/useCarver';
import {useCarverConfig} from '../../../hooks/useCarverConfig';
import {useCarverMutations} from '../../../hooks/useCarverMutations';
import {useEntity} from '../../../hooks/useEntity';
import {useEntityAlerts} from '../../../hooks/useEntityAlerts';
import {useEntityClaims} from '../../../hooks/useEntityClaims';
import {useEntityNotes, useEntityNoteMutations} from '../../../hooks/useEntityNotes';
import {useEntityPersonnel} from '../../../hooks/useEntityPersonnel';
import {useEntityIntelligenceSummary} from '../../../hooks/useEntityIntelligenceSummary';
import {useEntityCollectionRequirements} from '../../../hooks/useEntityCollectionRequirements';
import {useEntityProperties} from '../../../hooks/useEntityProperties';
import {useEntityPropertyMutations} from '../../../hooks/useEntityPropertyMutations';
import {useWikidataAutoFetch} from '../../../hooks/useWikidataAutoFetch';
import {uploadEntityAvatar} from '../../../firebase/firestore/entityAvatar';
import {
    AlertsTab,
    AliasesTab,
    CarverTab,
    ClaimsTab,
    CollectionRequirementsTab,
    PersonnelTab,
    PropertiesTab,
    SummaryTab,
} from '../../knowledgeBase/components';
import EntityDescriptionEditor from '../../knowledgeBase/components/EntityDescriptionEditor';
import UserMentionPopover from '../../../components/prosemirror-editor/components/UserMentionPopover';
import {useUserMentionPopover} from '../../../components/prosemirror-editor/hooks/useUserMentionPopover';
import {bustEntityAvatarCacheUrl, resolveChartEntityImageUrl, resolveEntityAvatarUrl} from '../../../utils/entityAvatarUrl';
import {getWikidataUrl, isLocalEntityRef} from '../../../utils/entityLinks';
import {getEntityTypeLabel} from '../../../utils/entityTypeMeta';
import {kbAliasesCrudEnabled} from '../../knowledgeBase/featureFlags';
import {MAX_PINNED_ALIASES, MAX_PINNED_PROPERTIES} from '../tldraw/customShapes/entityCardConstants';
import {
    pinAlias,
    pinKbLastUpdated,
    pinPropertySnapshot,
    refreshPinnedPropertySnapshots,
    unpinAlias,
    unpinKbLastUpdated,
    unpinPropertySnapshot,
} from '../tldraw/customShapes/entityCardPinUtils';
import {useEntityAliases} from '../../../hooks/useEntityAliases';
import {useEntityAliasMutations} from '../../../hooks/useEntityAliasMutations';
import useAiWidgetStore from '../../../stores/useAiWidgetStore';
import useChartAiBridgeStore from '../../../stores/useChartAiBridgeStore';
import {normalizeEntityRef} from '../ai/applyChartMutations';
import {COMMUNICATION_HUB_PANEL_WIDTH} from '../../../constants/communicationHubLayout';

const DRAWER_WIDTH = 1200;

function EntityChartPlacementAction({chartId, entityRef, label, entityShapeCount}) {
    const chartReady = useChartAiBridgeStore((state) => (
        chartId ? state.canApply(chartId) : false
    ));
    const applyMutations = useChartAiBridgeStore((state) => state.applyMutations);
    const [busy, setBusy] = React.useState(false);
    const [added, setAdded] = React.useState(false);
    const [error, setError] = React.useState(null);

    if (!chartId || !entityRef) {
        return null;
    }

    if (entityShapeCount > 0 || added) {
        return (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{color: 'success.main'}}>
                <CheckCircleOutlineIcon sx={{fontSize: 18}}/>
                <Typography variant="body2" sx={{fontWeight: 600, fontSize: 13}}>
                    Already on Chart
                </Typography>
            </Stack>
        );
    }

    const handleAdd = async () => {
        if (typeof applyMutations !== 'function') {
            setError('Open the chart editor to add entities.');
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const result = await applyMutations({
                schemaVersion: 1,
                chartId,
                summary: `Add ${label || entityRef}`,
                mutations: [{
                    op: 'add_entity_card',
                    entityRef,
                    label: label || entityRef,
                }],
                warnings: [],
                autoApply: false,
                preserveEditorSelection: true,
            });
            if (result?.failed?.length) {
                throw new Error(result.failed[0]?.error || 'Could not add entity');
            }
            setAdded(true);
        } catch (addError) {
            setError(addError?.message || 'Failed to add entity');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Stack spacing={0.25} alignItems="flex-start">
            <Button
                size="small"
                variant="outlined"
                disabled={!chartReady || busy}
                onClick={handleAdd}
                startIcon={busy ? <CircularProgress size={14}/> : null}
            >
                Add to Chart
            </Button>
            {error ? (
                <Typography variant="caption" color="error">{error}</Typography>
            ) : null}
        </Stack>
    );
}

function tabLabel(label, count, loading) {
    return loading ? label : `${label} (${count})`;
}

/** Optional T.number props must be finite numbers or omitted — never null (tldraw rejects null). */
function toFiniteNumber(value, fallback = 0) {
    return Number.isFinite(value) ? value : fallback;
}

function readCardPinState(getEntityCardProps) {
    const props = typeof getEntityCardProps === 'function' ? (getEntityCardProps() || {}) : {};
    return {
        pinnedProperties: Array.isArray(props.pinnedProperties) ? props.pinnedProperties : [],
        pinnedAliases: Array.isArray(props.pinnedAliases) ? props.pinnedAliases : [],
        carverPinned: Boolean(props.carverPinned),
        carverTotal: toFiniteNumber(props.carverTotal, 0),
        carverScoredFactorCount: toFiniteNumber(props.carverScoredFactorCount, 0),
        kbLastUpdatedPinned: Boolean(props.kbLastUpdatedPinned),
        kbLastUpdatedAt: typeof props.kbLastUpdatedAt === 'string' ? props.kbLastUpdatedAt : '',
        imageUrl: props.imageUrl || '',
    };
}

function EntityInfoDrawer({
    open,
    wikidataId,
    onClose,
    onExited,
    shapeId = null,
    openedFromPanel = false,
    entityShapeCount = 0,
    currentChartId = null,
    onUpdateEntityCardProps,
    getEntityCardProps,
}) {
    const [tab, setTab] = React.useState(0);
    const [refetchKey, setRefetchKey] = React.useState(0);
    const [propsRefetchKey, setPropsRefetchKey] = React.useState(0);
    const [aliasesRefetchKey, setAliasesRefetchKey] = React.useState(0);
    const [carverRefetchKey, setCarverRefetchKey] = React.useState(0);
    const [notesRefetchKey, setNotesRefetchKey] = React.useState(0);
    // Notes authors feed Personnel; bump both after post so the tab badge refreshes.
    const [personnelRefetchKey, setPersonnelRefetchKey] = React.useState(0);
    const [cardPins, setCardPins] = React.useState(() => readCardPinState(getEntityCardProps));
    const [avatarUploading, setAvatarUploading] = React.useState(false);
    const [avatarError, setAvatarError] = React.useState(null);
    const [localImageUrl, setLocalImageUrl] = React.useState('');
    const avatarInputRef = React.useRef(null);

    const handlePropertiesHydrated = React.useCallback(() => {
        setPropsRefetchKey((value) => value + 1);
    }, []);

    const handleCarverMutated = React.useCallback(() => {
        setCarverRefetchKey((value) => value + 1);
    }, []);

    const handleNotesMutated = React.useCallback(() => {
        setNotesRefetchKey((value) => value + 1);
        setPersonnelRefetchKey((value) => value + 1);
    }, []);

    const handleAliasesMutated = React.useCallback(() => {
        setAliasesRefetchKey((value) => value + 1);
    }, []);

    const {
        anchorEl: mentionAnchorEl,
        userData: mentionUserData,
        open: mentionPopoverOpen,
        handleUserMentionClick,
        handleClose: handleMentionPopoverClose,
    } = useUserMentionPopover();

    React.useEffect(() => {
        setTab(0);
        setAvatarError(null);
        setLocalImageUrl('');
    }, [wikidataId]);

    React.useEffect(() => {
        if (!open) return;
        setCardPins(readCardPinState(getEntityCardProps));
    }, [open, wikidataId, shapeId, getEntityCardProps]);

    const {entity, loading, error, notFound} = useEntity(wikidataId, refetchKey);
    const entityDocId = entity?.id || wikidataId || null;
    const {
        properties,
        revision: propertiesRevision,
        loading: propertiesLoading,
        error: propertiesError,
    } = useEntityProperties(entityDocId, propsRefetchKey);
    const {isFetching, isFetchingProps, fetchError, fetched, retry, canRetry} = useWikidataAutoFetch(
        wikidataId,
        entity,
        loading,
        error,
        notFound,
        {
            propertiesLoading,
            propertiesCount: properties.length,
            onPropertiesHydrated: handlePropertiesHydrated,
            // Chart drawer: hydrate missing cards immediately (canvas may already be importing).
            autoImportMissing: true,
        },
    );

    React.useEffect(() => {
        if (fetched) {
            setRefetchKey((value) => value + 1);
        }
    }, [fetched]);

    const awaitingFirestoreHydration = fetched && !loading && !entity && !error && !fetchError;

    const {
        claims: fintelClaims,
        reportHeadersById: fintelReportHeadersById,
        loading: fintelLoading,
    } = useEntityClaims(wikidataId, 'fintel');
    const {
        claims: draftClaims,
        reportHeadersById: draftReportHeadersById,
        loading: draftsLoading,
    } = useEntityClaims(wikidataId, 'draft');

    const {
        alerts,
        loading: alertsLoading,
        error: alertsError,
    } = useEntityAlerts(entityDocId);

    const {
        surfaceForms,
        wikidataAliases,
        loading: aliasesLoading,
        error: aliasesError,
    } = useEntityAliases(entityDocId, aliasesRefetchKey);

    const {
        createAlias,
        deleteAlias,
        saving: aliasMutationSaving,
        error: aliasMutationError,
    } = useEntityAliasMutations({
        entityId: entityDocId,
        onMutationSuccess: handleAliasesMutated,
    });

    const {
        factors: carverFactors,
        eligible: carverEligible,
        revision: carverRevision,
        total: carverTotal,
        scoredFactorCount: carverScoredCount,
        loading: carverLoading,
        error: carverError,
    } = useCarver(entityDocId, carverRefetchKey);
    const {entries: carverHistory, loading: carverHistoryLoading} =
        useCarverHistory(entityDocId, carverRefetchKey);
    const {minScore: carverMinScore, maxScore: carverMaxScore} = useCarverConfig();
    const {
        setFactor: setCarverFactor,
        saving: carverSaving,
        error: carverMutationError,
    } = useCarverMutations({
        entityId: entityDocId,
        revision: carverRevision,
        onMutationSuccess: handleCarverMutated,
    });

    const {notes, loading: notesLoading, error: notesError} =
        useEntityNotes(entityDocId, notesRefetchKey);
    const {
        postNote,
        updateNote,
        deleteNote,
        saving: notesSaving,
        error: notesMutationError,
    } = useEntityNoteMutations({
        entityId: entityDocId,
        onMutationSuccess: handleNotesMutated,
    });
    const {personnel, loading: personnelLoading, error: personnelError} =
        useEntityPersonnel(entityDocId, personnelRefetchKey);

    const collectionEntityId = entity?.wikidataId || wikidataId;
    const {
        icrs: collectionIcrs,
        eeis: collectionEeis,
        gaps: collectionGaps,
        cts: collectionCts,
        cms: collectionCms,
        iirs: collectionIirs,
        totalCount: collectionTotal,
        loading: collectionLoading,
        error: collectionError,
    } = useEntityCollectionRequirements(open ? collectionEntityId : null);

    // Only signed-in users may score / mutate: the API records who changed what and rejects anonymous writes.
    const canEdit = !!localStorage.getItem('access_token');
    const carverCanEdit = canEdit;
    const notesCanPost = canEdit;
    const summaryCanEdit = canEdit;
    const propertyCrudEnabled = canEdit;
    const aliasCrudEnabled = canEdit && kbAliasesCrudEnabled;
    const cardUpdateEnabled = typeof onUpdateEntityCardProps === 'function' && Boolean(shapeId);
    const cardPinControlsEnabled = cardUpdateEnabled && !(openedFromPanel && entityShapeCount > 1);
    const chartReady = useChartAiBridgeStore((state) => (
        currentChartId ? state.canApply(currentChartId) : false
    ));
    const applyMutations = useChartAiBridgeStore((state) => state.applyMutations);
    const chartEntities = useChartAiBridgeStore((state) => state.chartEntities);
    const chartConnections = useChartAiBridgeStore((state) => state.chartConnections);
    const chartPropertyActionsEnabled = Boolean(currentChartId)
        && chartReady
        && !(openedFromPanel && entityShapeCount > 1);
    const [addingPropertyId, setAddingPropertyId] = React.useState(null);
    const [propertyChartError, setPropertyChartError] = React.useState(null);

    const chartEntityIds = React.useMemo(
        () => chartEntities.map((entity) => entity.wikidataId).filter(Boolean),
        [chartEntities],
    );

    const {
        createProperty,
        updateProperty,
        deleteProperty,
        saving: propertyMutationSaving,
        error: propertyMutationError,
    } = useEntityPropertyMutations({
        entityId: entityDocId,
        revision: propertiesRevision,
        onMutationSuccess: handlePropertiesHydrated,
    });

    const applyCardProps = React.useCallback((partialProps) => {
        if (!cardUpdateEnabled) return;
        onUpdateEntityCardProps(partialProps);
        setCardPins((prev) => ({
            ...prev,
            ...partialProps,
            pinnedProperties: partialProps.pinnedProperties !== undefined
                ? partialProps.pinnedProperties
                : prev.pinnedProperties,
            pinnedAliases: partialProps.pinnedAliases !== undefined
                ? partialProps.pinnedAliases
                : prev.pinnedAliases,
        }));
    }, [cardUpdateEnabled, onUpdateEntityCardProps]);

    const syncPinnedFromLiveProperties = React.useCallback((liveProperties) => {
        if (!cardUpdateEnabled) return;
        const {next, changed} = refreshPinnedPropertySnapshots(cardPins.pinnedProperties, liveProperties);
        if (changed) {
            applyCardProps({pinnedProperties: next});
        }
    }, [applyCardProps, cardPins.pinnedProperties, cardUpdateEnabled]);

    React.useEffect(() => {
        if (!cardUpdateEnabled || !cardPins.carverPinned) return;
        const nextTotal = toFiniteNumber(carverTotal, 0);
        const nextScored = toFiniteNumber(carverScoredCount, 0);
        if (
            cardPins.carverTotal === nextTotal
            && cardPins.carverScoredFactorCount === nextScored
        ) {
            return;
        }
        applyCardProps({
            carverTotal: nextTotal,
            carverScoredFactorCount: nextScored,
        });
    }, [
        applyCardProps,
        cardPins.carverPinned,
        cardPins.carverScoredFactorCount,
        cardPins.carverTotal,
        cardUpdateEnabled,
        carverScoredCount,
        carverTotal,
    ]);

    const liveKbLastUpdatedAt = typeof entity?.kbLastUpdatedAt === 'string'
        ? entity.kbLastUpdatedAt
        : '';

    React.useEffect(() => {
        if (!cardUpdateEnabled || !cardPins.kbLastUpdatedPinned) return;
        if (cardPins.kbLastUpdatedAt === liveKbLastUpdatedAt) return;
        applyCardProps({kbLastUpdatedAt: liveKbLastUpdatedAt});
    }, [
        applyCardProps,
        cardPins.kbLastUpdatedAt,
        cardPins.kbLastUpdatedPinned,
        cardUpdateEnabled,
        liveKbLastUpdatedAt,
    ]);

    React.useEffect(() => {
        if (!open) {
            setPropertyChartError(null);
            setAddingPropertyId(null);
        }
    }, [open]);

    const regenerateAfterSourceChangeRef = React.useRef(() => {});

    const handleCreateProperty = React.useCallback(async (payload) => {
        const result = await createProperty(payload);
        regenerateAfterSourceChangeRef.current();
        return result;
    }, [createProperty]);

    const handleUpdateProperty = React.useCallback(async (propertyItemId, payload) => {
        const result = await updateProperty(propertyItemId, payload);
        regenerateAfterSourceChangeRef.current();
        if (cardUpdateEnabled && result?.item) {
            const merged = (properties || []).map((item) => (
                item.propertyItemId === propertyItemId
                    ? {...item, ...result.item, ...payload}
                    : item
            ));
            syncPinnedFromLiveProperties(merged);
        }
        return result;
    }, [cardUpdateEnabled, properties, syncPinnedFromLiveProperties, updateProperty]);

    const handleDeleteProperty = React.useCallback(async (propertyItemId) => {
        const result = await deleteProperty(propertyItemId);
        regenerateAfterSourceChangeRef.current();
        if (cardUpdateEnabled) {
            const {next, changed} = unpinPropertySnapshot(cardPins.pinnedProperties, propertyItemId);
            if (changed) {
                applyCardProps({pinnedProperties: next});
            }
        }
        return result;
    }, [applyCardProps, cardPins.pinnedProperties, cardUpdateEnabled, deleteProperty]);

    const handlePinProperty = React.useCallback((property) => {
        const {next, changed} = pinPropertySnapshot(cardPins.pinnedProperties, property);
        if (changed) {
            applyCardProps({pinnedProperties: next});
        }
    }, [applyCardProps, cardPins.pinnedProperties]);

    const handleUnpinProperty = React.useCallback((property) => {
        const {next, changed} = unpinPropertySnapshot(
            cardPins.pinnedProperties,
            property?.propertyItemId,
        );
        if (changed) {
            applyCardProps({pinnedProperties: next});
        }
    }, [applyCardProps, cardPins.pinnedProperties]);

    const handleAddPropertyToChart = React.useCallback(async (property) => {
        if (!chartPropertyActionsEnabled || !property?.valueEntityId) return;

        const sourceEntityRef = normalizeEntityRef(wikidataId || entity?.wikidataId);
        const valueEntityRef = normalizeEntityRef(property.valueEntityId);
        if (!sourceEntityRef || !valueEntityRef) return;

        if (typeof applyMutations !== 'function') {
            setPropertyChartError('Open the chart editor to add entities.');
            return;
        }

        const valueOnChart = chartEntities.some(
            (chartEntity) => normalizeEntityRef(chartEntity.wikidataId) === valueEntityRef,
        );
        const edgeLabel = property.propertyLabel || property.propertyId || 'related to';
        const sourceLabel = entity?.canonicalName || sourceEntityRef;
        const valueLabel = property.value || valueEntityRef;
        const anchorShapeId = cardUpdateEnabled ? shapeId : null;

        const mutations = [];
        if (!valueOnChart) {
            mutations.push({
                op: 'add_entity_card',
                entityRef: valueEntityRef,
                label: valueLabel,
                suggestedAnchor: `near:${sourceEntityRef}`,
                anchorShapeId: anchorShapeId || undefined,
                edgeLabel,
            });
        }
        // Property C on A with value B is drawn as B --C--> A ("B is C of A").
        mutations.push({
            op: 'add_arrow',
            fromEntityRef: valueEntityRef,
            toEntityRef: sourceEntityRef,
            label: edgeLabel,
            fromLabel: valueLabel,
            toLabel: sourceLabel,
            toShapeId: anchorShapeId || undefined,
            allowWideDetour: true,
        });

        setAddingPropertyId(property.propertyItemId || valueEntityRef);
        setPropertyChartError(null);
        try {
            const result = await applyMutations({
                schemaVersion: 1,
                chartId: currentChartId,
                summary: `Connect ${valueLabel} → ${sourceLabel}`,
                mutations,
                warnings: [],
                autoApply: false,
                preserveEditorSelection: true,
            });
            if (result?.failed?.length) {
                throw new Error(result.failed[0]?.error || 'Could not update chart');
            }
        } catch (addError) {
            setPropertyChartError(addError?.message || 'Failed to update chart');
        } finally {
            setAddingPropertyId(null);
        }
    }, [
        applyMutations,
        cardUpdateEnabled,
        chartEntityIds,
        chartPropertyActionsEnabled,
        currentChartId,
        entity?.canonicalName,
        entity?.wikidataId,
        shapeId,
        wikidataId,
    ]);

    const handlePinAlias = React.useCallback((alias) => {
        const {next, changed} = pinAlias(cardPins.pinnedAliases, alias);
        if (changed) {
            applyCardProps({pinnedAliases: next});
        }
    }, [applyCardProps, cardPins.pinnedAliases]);

    const handleUnpinAlias = React.useCallback((alias) => {
        const {next, changed} = unpinAlias(cardPins.pinnedAliases, alias);
        if (changed) {
            applyCardProps({pinnedAliases: next});
        }
    }, [applyCardProps, cardPins.pinnedAliases]);

    const handleCreateAlias = React.useCallback(async (alias) => {
        await createAlias(alias);
    }, [createAlias]);

    const handleDeleteAlias = React.useCallback(async (alias) => {
        const result = await deleteAlias(alias);
        if (cardUpdateEnabled) {
            const {next, changed} = unpinAlias(cardPins.pinnedAliases, alias);
            if (changed) {
                applyCardProps({pinnedAliases: next});
            }
        }
        return result;
    }, [applyCardProps, cardPins.pinnedAliases, cardUpdateEnabled, deleteAlias]);

    const handleToggleKbLastUpdatedPin = React.useCallback((shouldPin) => {
        applyCardProps(shouldPin
            ? pinKbLastUpdated(liveKbLastUpdatedAt)
            : unpinKbLastUpdated());
    }, [applyCardProps, liveKbLastUpdatedAt]);

    const handleToggleCarverPin = React.useCallback((shouldPin) => {
        if (shouldPin) {
            // Always send finite numbers — never null/undefined (sync schema: T.optional(T.number)).
            applyCardProps({
                carverPinned: true,
                carverTotal: toFiniteNumber(carverTotal, 0),
                carverScoredFactorCount: toFiniteNumber(carverScoredCount, 0),
            });
            return;
        }
        applyCardProps({
            carverPinned: false,
            carverTotal: 0,
            carverScoredFactorCount: 0,
        });
    }, [applyCardProps, carverScoredCount, carverTotal]);

    const handleSetCarverFactor = React.useCallback((factor, value) => {
        setCarverFactor(factor, value).catch(() => {
            /* surfaced through the hook's error state */
        });
    }, [setCarverFactor]);

    const handleAvatarSelected = React.useCallback(async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || !entityDocId) return;

        setAvatarUploading(true);
        setAvatarError(null);
        try {
            const {imageUrl} = await uploadEntityAvatar(entityDocId, file);
            const bustedImageUrl = bustEntityAvatarCacheUrl(imageUrl);
            setLocalImageUrl(bustedImageUrl);
            applyCardProps({imageUrl: bustedImageUrl});
            setRefetchKey((value) => value + 1);
        } catch (err) {
            setAvatarError(err);
        } finally {
            setAvatarUploading(false);
        }
    }, [applyCardProps, entityDocId]);

    // Notes live on Summary; Personnel follows Aliases; CARVER is last when eligible.
    const lastTabIndex = carverEligible ? 8 : 7;
    const activeTab = tab > lastTabIndex ? 0 : tab;
    const summaryTabActive = activeTab === 0;

    const {
        summary: intelligenceSummary,
        cached: intelligenceSummaryCached,
        stale: intelligenceSummaryStale,
        generatedAt: intelligenceSummaryGeneratedAt,
        sourceCounts: intelligenceSourceCounts,
        loading: intelligenceSummaryLoading,
        regenerating: intelligenceSummaryRegenerating,
        saving: intelligenceSummarySaving,
        error: intelligenceSummaryError,
        manuallyEdited: intelligenceSummaryManuallyEdited,
        regenerate: regenerateIntelligenceSummary,
        regenerateAfterSourceChange,
        saveSummary: saveIntelligenceSummary,
    } = useEntityIntelligenceSummary(entityDocId, {enabled: open && summaryTabActive});
    regenerateAfterSourceChangeRef.current = regenerateAfterSourceChange;

    const isLocal = isLocalEntityRef(wikidataId) || entity?.local === true || isLocalEntityRef(entity?.wikidataId);
    const wikidataUrl = isLocal ? null : getWikidataUrl(entity?.wikidataId || wikidataId);
    const relatedCountries = Array.isArray(entity?.relatedCountries) ? entity.relatedCountries : [];
    const showHydrationUi = !isLocal;
    const onChart = entityShapeCount > 0;
    const displayImageUrl = onChart
        ? resolveChartEntityImageUrl({
            wikidataId: entity?.wikidataId || wikidataId,
            imageUrl: localImageUrl || cardPins.imageUrl || '',
        })
        : resolveEntityAvatarUrl({
            wikidataId: entity?.wikidataId || wikidataId,
            imageUrl: localImageUrl || entity?.image || entity?.imageUrl || '',
        });
    const pinnedPropertyIds = cardPins.pinnedProperties.map((item) => item.propertyItemId);
    const aliasTabCount = wikidataAliases.length + surfaceForms.length;
    const aliasError = aliasCrudEnabled ? (aliasMutationError || aliasesError) : aliasesError;
    const aiPanelOpen = useAiWidgetStore((state) => state.open);

    return (
        <>
            <Drawer
                anchor="right"
                open={open && Boolean(wikidataId)}
                onClose={onClose}
                slotProps={{
                    root: {
                        disableEnforceFocus: true,
                        disableAutoFocus: true,
                        disableRestoreFocus: true,
                        disableScrollLock: true,
                    },
                    transition: {
                        onExited,
                    },
                    paper: {
                        sx: {
                            width: DRAWER_WIDTH,
                            maxWidth: aiPanelOpen
                                ? `calc(100vw - ${COMMUNICATION_HUB_PANEL_WIDTH}px)`
                                : '100vw',
                            marginRight: aiPanelOpen ? `${COMMUNICATION_HUB_PANEL_WIDTH}px` : 0,
                            display: 'flex',
                            flexDirection: 'column',
                        },
                    },
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between" px={2} py={1.5}>
                    <Typography variant="h6">Entity info</Typography>
                    <IconButton onClick={onClose} aria-label="Close entity info">
                        <CloseIcon/>
                    </IconButton>
                </Stack>
                <Divider/>

                <Box sx={{flex: 1, minHeight: 0, overflowY: 'auto'}}>
                    {(loading || (showHydrationUi && (isFetching || awaitingFirestoreHydration))) ? (
                        <Stack alignItems="center" spacing={1.5} py={6}>
                            <CircularProgress size={28}/>
                            {showHydrationUi && isFetching ? (
                                <Typography color="text.secondary">Importing...</Typography>
                            ) : null}
                            {showHydrationUi && awaitingFirestoreHydration && !isFetching ? (
                                <Typography color="text.secondary">Loading entity from Firestore...</Typography>
                            ) : null}
                        </Stack>
                    ) : null}

                    {showHydrationUi && fetchError ? (
                        <Box p={2}>
                            <Alert
                                severity="error"
                                action={
                                    <Button
                                        size="small"
                                        onClick={retry}
                                        disabled={!canRetry}
                                    >
                                        {canRetry ? 'Retry' : 'Max retries reached'}
                                    </Button>
                                }
                            >
                                Failed to fetch entity from Wikidata.
                            </Alert>
                        </Box>
                    ) : null}

                    {error ? (
                        <Box p={2}>
                            <Alert severity="error">Failed to load entity.</Alert>
                        </Box>
                    ) : null}

                    {!loading && !(showHydrationUi && (isFetching || awaitingFirestoreHydration)) && !entity && !error && !(showHydrationUi && fetchError) ? (
                        <Box p={2}>
                            <Alert severity="warning">
                                {isLocal
                                    ? 'Custom entity is not in the Knowledge Base yet.'
                                    : 'Entity is not in the Knowledge Base yet. Mentions are shown by Wikidata ID only.'}
                            </Alert>
                        </Box>
                    ) : null}

                    {entity ? (
                        <Stack spacing={2} p={2}>
                            <Stack direction="row" spacing={2} alignItems="flex-start">
                                <Box sx={{position: 'relative', flexShrink: 0}}>
                                    <Avatar
                                        src={displayImageUrl || undefined}
                                        alt={entity.canonicalName || entity.wikidataId || wikidataId}
                                        sx={{width: 72, height: 72, fontSize: 30, bgcolor: 'primary.main'}}
                                    >
                                        {(entity.canonicalName || entity.wikidataId || wikidataId || '?')[0].toUpperCase()}
                                    </Avatar>
                                    {canEdit ? (
                                        <>
                                            <input
                                                ref={avatarInputRef}
                                                type="file"
                                                accept="image/*"
                                                hidden
                                                onChange={handleAvatarSelected}
                                            />
                                            <IconButton
                                                size="small"
                                                aria-label={displayImageUrl ? 'Change image' : 'Upload image'}
                                                disabled={avatarUploading}
                                                onClick={() => avatarInputRef.current?.click()}
                                                sx={{
                                                    position: 'absolute',
                                                    right: -6,
                                                    bottom: -6,
                                                    bgcolor: 'background.paper',
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    '&:hover': {bgcolor: 'background.paper'},
                                                }}
                                            >
                                                {avatarUploading
                                                    ? <CircularProgress size={16}/>
                                                    : <PhotoCameraIcon fontSize="small"/>}
                                            </IconButton>
                                        </>
                                    ) : null}
                                </Box>

                                <Stack spacing={0.75} minWidth={0} flex={1}>
                                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <MuiLink
                                            component={RouterLink}
                                            to={`/kb/${entity.id || wikidataId}`}
                                            underline="hover"
                                            variant="h6"
                                            sx={{fontWeight: 700, color: 'text.primary', alignSelf: 'flex-start'}}
                                        >
                                            {entity.canonicalName || entity.wikidataId || wikidataId}
                                        </MuiLink>
                                        <EntityChartPlacementAction
                                            chartId={currentChartId}
                                            entityRef={wikidataId}
                                            label={entity.canonicalName || wikidataId}
                                            entityShapeCount={entityShapeCount}
                                        />
                                    </Stack>

                                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
                                        {entity.entityType ? (
                                            <Chip label={getEntityTypeLabel(entity.entityType) || entity.entityType} color="primary" size="small"
                                                  sx={{fontWeight: 600}}/>
                                        ) : null}
                                        {!isLocal && entity.wikidataId ? (
                                            <Chip label="Wikidata Entity" size="small" variant="outlined" color="info"/>
                                        ) : null}
                                        {isLocal && entity.source !== 'entities' ? (
                                            <Chip label="Custom Entity" size="small" variant="outlined"/>
                                        ) : null}
                                        {isLocal && entity.source === 'entities' ? (
                                            <Chip label="Entity Candidate" size="small" variant="outlined" color="warning"/>
                                        ) : null}
                                        {entity.confidence != null ? (
                                            <Chip
                                                label={`${Math.round(entity.confidence * 100)}% confidence`}
                                                variant="outlined"
                                                size="small"
                                            />
                                        ) : null}
                                        {!isLocal && wikidataUrl ? (
                                            <MuiLink
                                                href={wikidataUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                underline="hover"
                                                sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13}}
                                            >
                                                {entity.wikidataId || wikidataId} <OpenInNewIcon sx={{fontSize: 14}}/>
                                            </MuiLink>
                                        ) : null}
                                        {isLocal && (entity.wikidataId || wikidataId) ? (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    color: 'text.disabled',
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderRadius: 1,
                                                    px: 0.75,
                                                    py: 0.25,
                                                    lineHeight: 1.6,
                                                    userSelect: 'all',
                                                }}
                                                title="Entity ID"
                                            >
                                                {entity.wikidataId || wikidataId}
                                            </Typography>
                                        ) : null}
                                    </Stack>

                                </Stack>
                            </Stack>

                            {avatarError ? (
                                <Alert severity="error">
                                    {avatarError.message || 'Failed to upload image.'}
                                </Alert>
                            ) : null}

                            <EntityDescriptionEditor
                                entityId={entityDocId}
                                entity={entity}
                                canEdit={canEdit}
                                onSaved={() => setRefetchKey((value) => value + 1)}
                            />

                            {relatedCountries.length > 0 ? (
                                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                    {relatedCountries.map((country) => (
                                        <Chip key={country} size="small" label={country} variant="outlined"/>
                                    ))}
                                </Stack>
                            ) : null}

                            {entity.aliases?.length ? (
                                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                    {entity.aliases.map((alias) => (
                                        <Chip key={alias} label={alias} size="small" variant="outlined"/>
                                    ))}
                                </Stack>
                            ) : null}

                        </Stack>
                    ) : null}

                    {(propertiesError || alertsError || propertyMutationError || aliasError || propertyChartError) ? (
                        <Box px={2} pb={2}>
                            <Alert severity="error">
                                {propertyChartError
                                    || propertyMutationError?.message
                                    || aliasError?.message
                                    || propertiesError?.message
                                    || alertsError?.message
                                    || 'Failed to load entity metadata.'}
                            </Alert>
                        </Box>
                    ) : null}

                    <Tabs
                        value={activeTab}
                        onChange={(_event, value) => setTab(value)}
                        variant="fullWidth"
                        sx={{borderBottom: 1, borderColor: 'divider'}}
                    >
                        <Tab label="Summary"/>
                        <Tab label={tabLabel('In FINTEL', fintelClaims.length, fintelLoading)}/>
                        <Tab label={tabLabel('In Drafts', draftClaims.length, draftsLoading)}/>
                        <Tab label={tabLabel('Collection', collectionTotal, collectionLoading)}/>
                        <Tab label={tabLabel('Properties', properties.length, propertiesLoading || isFetchingProps)}/>
                        <Tab label={tabLabel('Alerts', alerts.length, alertsLoading)}/>
                        <Tab label={tabLabel('Aliases', aliasTabCount, aliasesLoading)}/>
                        <Tab label={tabLabel('Personnel', personnel.length, personnelLoading)}/>
                        {carverEligible && <Tab label="CARVER"/>}
                    </Tabs>

                    <Box sx={{pb: 2}}>
                        {activeTab === 0 ? (
                            <SummaryTab
                                summary={intelligenceSummary}
                                loading={intelligenceSummaryLoading}
                                regenerating={intelligenceSummaryRegenerating}
                                saving={intelligenceSummarySaving}
                                error={intelligenceSummaryError}
                                cached={intelligenceSummaryCached}
                                stale={intelligenceSummaryStale}
                                manuallyEdited={intelligenceSummaryManuallyEdited}
                                generatedAt={intelligenceSummaryGeneratedAt}
                                kbLastUpdatedAt={entity?.kbLastUpdatedAt || null}
                                pinEnabled={cardPinControlsEnabled}
                                kbLastUpdatedPinned={cardPins.kbLastUpdatedPinned}
                                onToggleKbLastUpdatedPin={handleToggleKbLastUpdatedPin}
                                sourceCounts={intelligenceSourceCounts}
                                onRegenerate={regenerateIntelligenceSummary}
                                canEdit={summaryCanEdit}
                                onSave={summaryCanEdit ? saveIntelligenceSummary : undefined}
                                notes={notes}
                                notesLoading={notesLoading}
                                notesError={notesError}
                                notesCanPost={notesCanPost}
                                onPostNote={postNote}
                                onUpdateNote={notesCanPost ? updateNote : undefined}
                                onDeleteNote={notesCanPost ? deleteNote : undefined}
                                notesSaving={notesSaving}
                                notesMutationError={notesMutationError}
                            />
                        ) : null}

                        {activeTab === 1 ? (
                            <ClaimsTab
                                claims={fintelClaims}
                                reportHeadersById={fintelReportHeadersById}
                                loading={fintelLoading}
                                emptyMessage="This entity has no mentions in FINTEL reports."
                                onAuthorClick={handleUserMentionClick}
                                currentEntityWikidataId={entity?.wikidataId || wikidataId}
                            />
                        ) : null}

                        {activeTab === 2 ? (
                            <ClaimsTab
                                claims={draftClaims}
                                reportHeadersById={draftReportHeadersById}
                                loading={draftsLoading}
                                emptyMessage="This entity has no mentions in draft reports."
                                onAuthorClick={handleUserMentionClick}
                                currentEntityWikidataId={entity?.wikidataId || wikidataId}
                            />
                        ) : null}

                        {activeTab === 3 ? (
                            <CollectionRequirementsTab
                                icrs={collectionIcrs}
                                eeis={collectionEeis}
                                gaps={collectionGaps}
                                cts={collectionCts}
                                cms={collectionCms}
                                iirs={collectionIirs}
                                loading={collectionLoading}
                                error={collectionError}
                            />
                        ) : null}

                        {activeTab === 4 ? (
                            <PropertiesTab
                                properties={properties}
                                loading={propertiesLoading || isFetchingProps}
                                crudEnabled={propertyCrudEnabled}
                                onCreateProperty={propertyCrudEnabled ? handleCreateProperty : undefined}
                                onUpdateProperty={propertyCrudEnabled ? handleUpdateProperty : undefined}
                                onDeleteProperty={propertyCrudEnabled ? handleDeleteProperty : undefined}
                                saving={propertyMutationSaving}
                                error={propertyMutationError}
                                pinEnabled={cardPinControlsEnabled}
                                pinnedPropertyIds={pinnedPropertyIds}
                                onPinProperty={handlePinProperty}
                                onUnpinProperty={handleUnpinProperty}
                                maxPinnedProperties={MAX_PINNED_PROPERTIES}
                                chartActionsEnabled={chartPropertyActionsEnabled}
                                chartReady={chartReady}
                                sourceEntityRef={wikidataId || entity?.wikidataId}
                                chartEntityIds={chartEntityIds}
                                chartConnections={chartConnections}
                                onAddPropertyToChart={handleAddPropertyToChart}
                                addingPropertyId={addingPropertyId}
                            />
                        ) : null}

                        {activeTab === 5 ? (
                            <AlertsTab alerts={alerts} loading={alertsLoading}/>
                        ) : null}

                        {activeTab === 6 ? (
                            <AliasesTab
                                wikidataAliases={wikidataAliases}
                                surfaceForms={surfaceForms}
                                loading={aliasesLoading}
                                onCreateAlias={aliasCrudEnabled ? handleCreateAlias : undefined}
                                onDeleteAlias={aliasCrudEnabled ? handleDeleteAlias : undefined}
                                saving={aliasMutationSaving}
                                error={aliasMutationError}
                                crudEnabled={aliasCrudEnabled}
                                pinEnabled={cardPinControlsEnabled}
                                pinnedAliases={cardPins.pinnedAliases}
                                onPinAlias={handlePinAlias}
                                onUnpinAlias={handleUnpinAlias}
                                maxPinnedAliases={MAX_PINNED_ALIASES}
                            />
                        ) : null}

                        {activeTab === 7 ? (
                            <PersonnelTab
                                personnel={personnel}
                                loading={personnelLoading}
                                error={personnelError}
                            />
                        ) : null}

                        {activeTab === 8 && carverEligible ? (
                            <CarverTab
                                factors={carverFactors}
                                total={carverTotal}
                                scoredFactorCount={carverScoredCount}
                                minScore={carverMinScore}
                                maxScore={carverMaxScore}
                                loading={carverLoading}
                                error={carverMutationError || carverError}
                                saving={carverSaving}
                                canEdit={carverCanEdit}
                                onSetFactor={handleSetCarverFactor}
                                history={carverHistory}
                                historyLoading={carverHistoryLoading}
                                pinEnabled={cardPinControlsEnabled}
                                carverPinned={cardPins.carverPinned}
                                onToggleCarverPin={handleToggleCarverPin}
                            />
                        ) : null}
                    </Box>
                </Box>
            </Drawer>

            <UserMentionPopover
                open={mentionPopoverOpen}
                anchorEl={mentionAnchorEl}
                onClose={handleMentionPopoverClose}
                userData={mentionUserData}
            />
        </>
    );
}

export default EntityInfoDrawer;
